// Renders a short-lived file download as a styled card (reuses the clarify-card
// look). Emitted by the backend as a `download` SSE event when a result is too
// long to show inline. The link is client-scoped + expires (default 5 min), so
// the card is intentionally ephemeral — it isn't replayed on thread reload.

import type { DownloadInfo } from './types'

// The backend emits either an ABSOLUTE url (when NETWATCH_INGEST_URL has a scheme)
// or a RELATIVE "/downloads/<token>?..." path. A relative path MUST be resolved
// against the API origin the widget talks to (`apiEndpoint`) — never the embedding
// page's origin (a Vite dev server, the host UI, …), which would return that
// page's HTML instead of the file.
function resolveDownloadUrl(
  url: string | undefined,
  apiEndpoint: string | undefined,
): string | undefined {
  if (!url || /^https?:\/\//i.test(url)) return url
  if (apiEndpoint) {
    try {
      return new URL(url, apiEndpoint).href
    } catch {
      const base = String(apiEndpoint).replace(/\/+$/, '')
      return `${base}${url.startsWith('/') ? '' : '/'}${url}`
    }
  }
  return url
}

interface Props {
  url?: DownloadInfo['url']
  filename?: DownloadInfo['filename']
  row_count?: number
  expires_in_seconds?: number
  label?: string
  apiEndpoint?: string
}

export function DownloadCard({ url, filename, row_count, expires_in_seconds, label, apiEndpoint }: Props) {
  const href = resolveDownloadUrl(url, apiEndpoint)
  const mins = expires_in_seconds ? Math.round(expires_in_seconds / 60) : null
  const meta = [
    row_count != null ? `${row_count} rows` : null,
    mins ? `expires in ${mins} min` : null,
  ].filter(Boolean).join(' · ')

  return (
    <div className="nw-clarify nw-download">
      <div className="nw-clarify-chip">Download</div>
      {meta && <div className="nw-clarify-text">{meta}</div>}
      <a
        className="nw-clarify-submit nw-download-btn"
        href={href}
        download={filename || true}
        target="_blank"
        rel="noopener noreferrer"
      >
        {label || 'Download CSV'}
      </a>
    </div>
  )
}
